import {
	HttpClient,
	HttpResponse
}                                        from '@angular/common/http';
import { Injectable }                    from '@angular/core';
import { AccountInitialization }         from '@app/modules/admin-tenant/models/account.model';
import {
	Tenant,
	TenantForm
}                                        from '@app/modules/admin-tenant/models/tenant.model';
import {
	IAccountTenantHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                                        from '@core/interfaces/api.interface';
import { AbstractApiService }            from '@http/abstract-api.service';
import { TenantApiSanitizerService }     from '@http/admin/tenant-api-sanitizer.service';
import { TransitionService }             from '@http/transition.service';
import { GetTenantTenantsRequestParams } from '@openApiGenerated/admin-api';
import {
	AccountInitializationDTO,
	AccountsApiClient,
	AccountTenantBodyDTO,
	ClsAccountInitializationDTO,
	ClsAccountTenantBodyDTO,
	ClsTenantStatusDTO,
	GetTenantRequestParams,
	GetTenantsRequestParams,
	InitializeRequestParams,
	TenantDTO,
	TenantsResponseDTO,
	TenantStatusDTO,
	UpdateTenantRequestParams,
	UpdateTenantStatusRequestParams
}                                        from '@openApiGenerated/management-api';
import { LoggerService }                 from '@shared/services/logger.service';
import { Observable }                    from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class AccountTenantApiService extends AbstractApiService {

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly accountsApiClient: AccountsApiClient,
		private readonly transition: TransitionService,
		private readonly tenantApiSanitizerService: TenantApiSanitizerService
	) {
		super();
		this.http = http;
	}

	// SANITIZER ######################################
	get sanitizer(): TenantApiSanitizerService {
		return this.tenantApiSanitizerService;
	}


	// LIST TYPE REQUESTS #############################
	public list(): Observable<RequestCompleteListState<Tenant>> {
		this.logger.trace( 'pre-list call' );
		const requestParam: GetTenantsRequestParams = {
			filter: null,
			search: null,
			status: null,
			pageSize: null,
			pageNumber: null,
			sort: 'lastModifiedDate,desc'
		};

		const req$ = this.accountsApiClient.getTenants( requestParam ) as any;
		return this.transition.handleListRequestResponse<TenantsResponseDTO, Tenant>(
			req$, Tenant, this, this.sanitizer.sanitizeApiResponse
		);
	}

	public search( params: IAccountTenantHttpParams, throwError = false, tenantId?: string ): Observable<RequestCompleteListState<Tenant>> {
		this.logger.trace( 'pre-search call: ', URL );

		let req$;
		if ( tenantId ) {
			// searching for Tenants subaccounts
			const requestParamTenant: GetTenantTenantsRequestParams = {
				tenantId,
				pageSize: params?.paging?.pageSize,
				pageNumber: params?.paging?.pageNumber,
				sort: params?.sort
			};
			req$ = this.accountsApiClient.getTenantTenants( requestParamTenant ) as any;
		} else {
			// searching for Tenants
			const requestParam: GetTenantsRequestParams = {
				filter: params?.filter,
				search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.search ),
				status: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status ),
				pageSize: params?.paging?.pageSize,
				pageNumber: params?.paging?.pageNumber,
				sort: params?.sort,
				tenantIds: params?.tenantIds,
				customerIdentifier: params?.customerIdentifier,
				fiscalContractId: params?.fiscalContractId,
				archivingContractId: params?.archivingContractId,
				contractMonth: params?.contractMonth
			};

			// searching for tenants parentTenants
			if ( requestParam.tenantIds?.length ) {
				delete requestParam.search;
				delete requestParam.status;
				delete requestParam.sort;
				delete requestParam.filter;
			}

			req$ = this.accountsApiClient.getTenants( requestParam ) as any;
		}
		return this.transition.handleListRequestResponse<TenantsResponseDTO, Tenant>(
			req$,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public initialize( accountInitialization: AccountInitialization, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-initialize call: ', { ...accountInitialization } );
		const accountInitializationDTO = this.transition.prepareEntityRequest<AccountInitialization, AccountInitializationDTO>(
			accountInitialization, ClsAccountInitializationDTO, this, this.sanitizer.sanitizeForAccountApiRequest
		);
		const requestParam: InitializeRequestParams = {
			accountInitializationDTO
		};

		const req$ = this.accountsApiClient.initialize( requestParam );
		return this.transition.handleEntityRequestResponse<TenantDTO | HttpResponse<TenantDTO>, Tenant>(
			req$,
			RequestActionEnum.CREATE,
			Tenant,
			this,
			null,
			null,
			null,
			null,  // forceEntity,
			throwError
		);
	}

	public read( id: string, withFullResponse?: boolean, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-read call: ', id );
		const requestParam: GetTenantRequestParams = {
			tenantId: id
		};

		const req$ = this.accountsApiClient.getTenant( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<TenantDTO | HttpResponse<TenantDTO>, Tenant>(
			req$,
			RequestActionEnum.READ,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public update( tenantForm: TenantForm, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-update call: ', { ...tenantForm } );
		const accountTenantBodyDTO = this.transition.prepareEntityRequest<TenantForm, AccountTenantBodyDTO>(
			tenantForm, ClsAccountTenantBodyDTO, this, this.sanitizer.sanitizeForAccountApiRequest
		);
		const requestParam: UpdateTenantRequestParams = {
			tenantId: tenantForm.id,
			ifMatch: tenantForm.etag,
			accountTenantBodyDTO
		};

		const req$ = this.accountsApiClient.updateTenant( requestParam, 'response' );
		return this.transition.handleEntityRequestResponse<TenantDTO | HttpResponse<TenantDTO>, Tenant>(
			req$,
			RequestActionEnum.UPDATE,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public changeStatus( tenant: Tenant, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-update call: ', { ...tenant } );
		const tenantStatusDTO = this.transition.prepareEntityRequest<Tenant, TenantStatusDTO>(
			tenant, ClsTenantStatusDTO, this, this.sanitizer.sanitizeForAccountApiRequest
		);
		const requestParam: UpdateTenantStatusRequestParams = {
			tenantId: tenant.id,
			ifMatch: tenant.etag,
			tenantStatusDTO
		};

		const req$ = this.accountsApiClient.updateTenantStatus( requestParam );
		return this.transition.handleEntityRequestResponse<TenantDTO, Tenant>(
			req$,
			RequestActionEnum.UPDATE,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
